import { t } from '../i18n/i18n';
/**
 * "YOU ARE DOWN" — the downed window's screen (amendment 2026-09-01,
 * `design/gdd/run-lifecycle.md` Core Rule #4).
 *
 * Until now a lethal hit went straight to the run summary, which is why
 * reviving "did not work": by the time a squadmate reached the body, the
 * player behind it was already reading how their run had ended. Death is now
 * two phases, and this is the first one — the seconds in which somebody can
 * still pick you up.
 *
 * It deliberately does *not* take the screen the way `run-end-overlay.ts`
 * does. The whole point of being downed is watching whether anyone is coming,
 * so the world stays visible and this is a band at the bottom with a clock on
 * it. `pointer-events-none` throughout: there is nothing to click, and a
 * transparent overlay that swallowed input would be worse than useless to the
 * squadmate still fighting over your body.
 */

/** Below the crosshair and the combat HUD, above the world. */
const Z = 'z-[1200]';

export class DownedOverlay {
  private readonly root: HTMLDivElement;
  private readonly clock: HTMLDivElement;
  private readonly note: HTMLDivElement;
  private raf: number | null = null;
  private endsAtMs = 0;
  private rescuer: { nickname: string; lifting: boolean } | null = null;
  private readonly reducedMotion: boolean;

  constructor(container: HTMLElement) {
    this.reducedMotion = typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    this.root = document.createElement('div');
    this.root.className = [
      'fixed', 'inset-x-0', 'bottom-0', Z,
      'pointer-events-none', 'select-none', 'text-center',
      'px-4', 'pb-10', 'pt-24', 'hidden',
    ].join(' ');
    // A crimson wash from the floor up, so being down reads before any word is.
    this.root.style.background = 'linear-gradient(0deg, rgba(90,0,0,0.62) 0%, rgba(11,10,18,0.35) 55%, transparent 100%)';

    const title = document.createElement('div');
    title.className = 'font-heading text-4xl sm:text-6xl font-black';
    title.style.color = '#ede0cd';
    title.style.textShadow = '0 0 18px rgba(220,60,40,0.75), 0 2px 10px rgba(0,0,0,0.9)';
    title.textContent = t('You are down');

    this.note = document.createElement('div');
    this.note.className = 'mt-2 font-heading italic text-base sm:text-lg';

    this.clock = document.createElement('div');
    this.clock.className = 'mt-1 font-heading text-2xl sm:text-3xl font-bold tabular-nums';
    this.clock.style.color = '#ede0cd';

    this.root.append(title, this.note, this.clock);
    container.appendChild(this.root);
  }

  /**
   * Shown only when a rescue is actually possible (2026-09-03).
   *
   * This used to take a `canBeRevived` flag and, when it was false, run the
   * full twenty-second clock under the words "Nobody is left to reach you" —
   * documented at the time as giving "a solo delver the clock without the
   * false promise of rescue".
   *
   * User: "if the whole team is dead and I was the last one left, no one can
   * physically revive me. Therefore, if I'm the last one remaining, there
   * shouldn't be a 20-second countdown."
   *
   * Correct, and a clock is not the neutral thing that reasoning assumed: a
   * number counting down says *waiting is the move*. The server had already
   * decided otherwise — `deathPhase` finalises the run on the next tick when
   * nobody can reach you — so the screen was counting toward an event that had
   * been cancelled before it was drawn. The window is now gated server-side
   * and this is only ever called when someone really could come.
   *
   * @param endsAtMs  When the window closes, on the same clock
   *                  `performance.now()` reads — the caller converts from
   *                  server time, because it is the one holding the offset.
   */
  show(endsAtMs: number): void {
    this.endsAtMs = endsAtMs;
    this.rescuer = null;
    this.renderNote();
    this.root.classList.remove('hidden');
    this.tick();
  }

  /**
   * Who is on their way, live (2026-09-02).
   *
   * Bots revive now (`sim/bot-revive.ts`), and they are allowed to fail — a
   * squadmate with a monster on it keeps fighting rather than kneeling. That
   * choice only reads as a story if you can watch it happen. A flat "a
   * squadmate can still reach you" for twenty seconds is indistinguishable
   * from nobody ever coming, which is exactly the bug this replaces.
   *
   * @param nickname Who is coming, or null when nobody is free.
   * @param lifting  True once they are kneeling and channelling, rather than
   *                 still running over. Different sentence, because the second
   *                 one is four seconds from over and the first might not be.
   */
  setRescuer(nickname: string | null, lifting: boolean): void {
    this.rescuer = nickname ? { nickname, lifting } : null;
    this.renderNote();
  }

  private renderNote(): void {
    if (this.rescuer) {
      this.note.textContent = this.rescuer.lifting
        ? t('{name} is lifting you', { name: this.rescuer.nickname })
        : t('{name} is coming', { name: this.rescuer.nickname });
      // Brass rather than the resting gold: someone moving toward you is the
      // one hopeful thing on this screen and should not look like a label.
      this.note.style.color = '#f3d089';
      return;
    }
    // Only ever "not yet" — a body with nobody left to reach it never gets
    // this screen at all now. See `show`.
    this.note.textContent = t('Nobody is free to reach you yet');
    this.note.style.color = '#ac9b83';
  }

  hide(): void {
    this.root.classList.add('hidden');
    if (this.raf !== null) { cancelAnimationFrame(this.raf); this.raf = null; }
  }

  private tick = (): void => {
    const remainingMs = Math.max(0, this.endsAtMs - performance.now());
    // Ceil so the number never reads 0 while a revive is still possible — the
    // one moment the display and the rule must not disagree. Mirrors
    // `downedSecondsRemaining` on the server.
    const seconds = Math.ceil(remainingMs / 1000);
    this.clock.textContent = String(seconds);
    // The last five seconds pulse, because that is when a squadmate deciding
    // whether to come needs to feel the clock rather than read it.
    this.clock.style.color = seconds <= 5 ? '#e74c3c' : '#ede0cd';
    if (!this.reducedMotion && seconds <= 5 && remainingMs > 0) {
      const phase = (remainingMs % 1000) / 1000;
      this.clock.style.transform = `scale(${1 + 0.12 * Math.max(0, 1 - phase * 3)})`;
    } else {
      this.clock.style.transform = 'scale(1)';
    }
    if (remainingMs <= 0) { this.raf = null; return; }
    this.raf = requestAnimationFrame(this.tick);
  };

  dispose(): void {
    this.hide();
    this.root.remove();
  }
}
